import { useEffect, useState } from 'react'
import {
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Ban,
  Download,
  FileX2,
  ListChecks,
  Loader2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { PaginatedTable } from '@/components/common/PaginatedTable'
import {
  useProductListRejectedItems,
  useProductListIssues,
  useProductListErrorReport,
  type HierarchyCorrectionMapping,
} from '@/hooks/useProductLists'
import { toast } from '@/stores/useToastStore'
import {
  isHierarchyCorrection,
  type ProductListRead,
  type ProductListItemRead,
  type ValidationError,
} from '@/api/productLists'
import type { PlanItem } from '@/api/planItems'

interface ValidItemsPagination {
  items: PlanItem[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
  isLoading: boolean
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}

interface ImportResultsSummaryProps {
  validRowsLabel?: string
  isUpdate?: boolean
  productList: ProductListRead
  validPagination: ValidItemsPagination
  /** Notifies the parent (which owns the "Add Items" action) whenever the
   * blocking-mismatch state changes, so it can gate the import. */
  onMismatchStatusChange?: (status: {
    hasMismatches: boolean
    isLoading: boolean
  }) => void
}

const VALID_COLUMNS: {
  key: keyof PlanItem
  header: string
  width?: string
}[] = [
  { key: 'skuNumber', header: 'SKU #', width: '120px' },
  { key: 'description', header: 'Description' },
]

function ImportIssue({ error: err }: { error: ValidationError }) {
  return isHierarchyCorrection(err) && err.corrected_value != null ? (
    <div className="space-y-2 py-1 text-sm">
      <p className="font-medium text-foreground">{err.column}</p>
      <div className="grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-center">
        <div className="min-w-0 rounded-md border bg-background/50 px-3 py-2">
          <p className="mb-1 text-xs text-muted-foreground">In your file</p>
          <p className="[overflow-wrap:anywhere] text-muted-foreground">
            {err.value ?? '—'}
          </p>
        </div>
        <ArrowRight
          aria-hidden="true"
          className="hidden h-4 w-4 text-muted-foreground sm:block"
        />
        <div className="min-w-0 rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
          <p className="mb-1 text-xs text-blue-700">Corrected to</p>
          <p className="[overflow-wrap:anywhere] font-medium text-foreground">
            {err.corrected_value}
          </p>
        </div>
      </div>
    </div>
  ) : (
    <div className="text-sm">
      <span className="font-medium text-[#1C1C1C]">{err.column}</span>
      {err.value !== null && err.value !== '' && (
        <span className="text-[#556374]">
          {' '}
          = &quot;{String(err.value)}&quot;
        </span>
      )}
      <p className="text-[#556374] text-[13px]">{err.message}</p>
    </div>
  )
}

function renderErrors(_: unknown, item: ProductListItemRead) {
  if (!item.errors || item.errors.length === 0) return '—'
  return (
    <div className="space-y-1">
      {item.errors.map((err, i) => (
        <ImportIssue key={i} error={err} />
      ))}
    </div>
  )
}

function makeIssueColumns(header: string) {
  return [
    { key: 'row_number' as const, header: 'Row', width: '60px' },
    { key: 'errors' as const, header, render: renderErrors },
  ]
}

const REJECTED_COLUMNS = makeIssueColumns('Issue')
const MISMATCH_COLUMNS = makeIssueColumns('Mismatch')
const WARNING_COLUMNS = makeIssueColumns('Warning')
const CORRECTION_COLUMNS = [
  {
    key: 'correction' as const,
    header: 'Hierarchy correction',
    render: (_: unknown, mapping: HierarchyCorrectionMapping) => (
      <div className="space-y-2">
        <ImportIssue error={mapping.correction} />
        {mapping.rowNumbers.length > 10 ? (
          <details className="text-xs font-normal text-muted-foreground">
            <summary className="cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              Applied to {mapping.rowNumbers.length} rows
            </summary>
            <p className="mt-2 max-h-28 overflow-y-auto leading-5">
              Rows: {mapping.rowNumbers.join(', ')}
            </p>
          </details>
        ) : (
          <p className="text-xs font-normal text-muted-foreground">
            {mapping.rowNumbers.length === 1 ? 'Row' : 'Rows'}:{' '}
            {mapping.rowNumbers.join(', ')}
          </p>
        )}
      </div>
    ),
  },
]

type IssueTableProps = {
  /** The current page's items only (already sliced, client- or server-side). */
  data: ProductListItemRead[]
  /** Total item count across all pages, for the pager controls. */
  totalCount: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  isLoading?: boolean
  emptyMessage: string
  columns: typeof REJECTED_COLUMNS
}

function IssueTable({
  data,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  isLoading,
  emptyMessage,
  columns,
}: IssueTableProps) {
  return (
    <div className="border rounded-lg overflow-hidden [&_td]:align-top">
      <PaginatedTable<ProductListItemRead>
        columns={
          columns as unknown as {
            key: keyof ProductListItemRead
            header: string
            width?: string
            render?: (
              value: ProductListItemRead[keyof ProductListItemRead],
              item: ProductListItemRead
            ) => React.ReactNode
          }[]
        }
        data={data}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={Math.max(1, Math.ceil(totalCount / pageSize))}
        pageSizeOptions={[5, 10, 25]}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        isLoading={isLoading}
        getRowKey={item => item.id}
        emptyMessage={emptyMessage}
      />
    </div>
  )
}

/** Client-side pagination over an already-fully-fetched array. */
function sliceForPage<T>(data: T[], page: number, pageSize: number) {
  const start = (page - 1) * pageSize
  return data.slice(start, start + pageSize)
}

export function ImportResultsSummary({
  validRowsLabel = 'Rows imported',
  isUpdate = false,
  productList,
  validPagination,
  onMismatchStatusChange,
}: ImportResultsSummaryProps) {
  const [rejectedPage, setRejectedPage] = useState(1)
  const [rejectedPageSize, setRejectedPageSize] = useState(5)
  const [mismatchPage, setMismatchPage] = useState(1)
  const [mismatchPageSize, setMismatchPageSize] = useState(5)
  const [warningPage, setWarningPage] = useState(1)
  const [warningPageSize, setWarningPageSize] = useState(5)
  const [correctionPage, setCorrectionPage] = useState(1)
  const [correctionPageSize, setCorrectionPageSize] = useState(5)

  const hasRejected = productList.invalid_rows > 0

  const { data: rejectedData, isLoading: rejectedLoading } =
    useProductListRejectedItems(
      productList.id,
      rejectedPage,
      rejectedPageSize,
      hasRejected
    )

  const { data: issues, isLoading: issuesLoading } = useProductListIssues(
    productList.id,
    productList.valid_rows,
    true
  )

  const mismatchItems = issues?.mismatchItems ?? []
  const correctionMappings = issues?.correctionMappings ?? []
  const otherWarningItems = issues?.otherWarningItems ?? []
  const hasMismatches = mismatchItems.length > 0
  const hasWarnings = otherWarningItems.length > 0
  const hasCorrections = correctionMappings.length > 0
  const hasIssues =
    hasRejected || hasMismatches || hasWarnings || hasCorrections

  const acceptedCount = productList.valid_rows - mismatchItems.length
  const rejectedCount = productList.invalid_rows + mismatchItems.length

  const mismatchPageItems = sliceForPage(
    mismatchItems,
    mismatchPage,
    mismatchPageSize
  )
  const warningPageItems = sliceForPage(
    otherWarningItems,
    warningPage,
    warningPageSize
  )
  const correctionPageItems = sliceForPage(
    correctionMappings,
    correctionPage,
    correctionPageSize
  )

  // Let the caller (which owns the "Add Items" action) know whenever the
  // blocking-mismatch state changes, so it can gate the import.
  useEffect(() => {
    onMismatchStatusChange?.({ hasMismatches, isLoading: issuesLoading })
  }, [hasMismatches, issuesLoading, onMismatchStatusChange])

  const errorReportMutation = useProductListErrorReport()
  const handleDownloadErrorReport = () => {
    errorReportMutation.mutate(productList.id, {
      onSuccess: data => {
        window.open(data.download_url, '_blank')
      },
      onError: (error: Error) => {
        toast.error(`Failed to download error report: ${error.message}`)
      },
    })
  }

  const defaultTab = hasRejected
    ? 'rejected'
    : hasMismatches
      ? 'mismatches'
      : hasCorrections
        ? 'corrections'
        : 'warnings'

  const validItemsTable = (
    <div className="border rounded-lg overflow-hidden">
      <PaginatedTable<PlanItem>
        columns={VALID_COLUMNS}
        data={validPagination.items}
        page={validPagination.page}
        pageSize={validPagination.pageSize}
        totalCount={validPagination.totalCount}
        totalPages={validPagination.totalPages}
        pageSizeOptions={[5, 10, 25]}
        onPageChange={validPagination.onPageChange}
        onPageSizeChange={validPagination.onPageSizeChange}
        isLoading={validPagination.isLoading}
        getRowKey={item => item.id}
        emptyMessage="No items found in the uploaded file."
      />
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white p-3">
          <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
          <div>
            <p className="text-[18px] font-semibold text-[#1C1C1C]">
              {acceptedCount}
            </p>
            <p className="text-[12px] text-[#556374]">{validRowsLabel}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white p-3">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
          <div>
            <p className="text-[18px] font-semibold text-[#1C1C1C]">
              {rejectedCount}
            </p>
            <p className="text-[12px] text-[#556374]">Rows rejected</p>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-[#E5E7EB] bg-white p-3">
          <FileX2 className="h-5 w-5 text-[#556374] shrink-0" />
          <div>
            <p className="text-[18px] font-semibold text-[#1C1C1C]">
              {productList.skipped_rows}
            </p>
            <p className="text-[12px] text-[#556374]">Empty rows skipped</p>
          </div>
        </div>
      </div>

      {issuesLoading && (
        <div className="flex items-center gap-2 text-[13px] text-[#556374]">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Checking values against dropdown and hierarchy options…
        </div>
      )}

      {hasMismatches && (
        <Alert variant="destructive">
          <Ban aria-hidden="true" className="h-4 w-4" />
          <AlertDescription>
            <span className="font-medium">
              This file can&apos;t be imported yet — {mismatchItems.length} row
              {mismatchItems.length !== 1 ? 's' : ''} have values that
              don&apos;t match an existing option. Fix these values in your file
              and re-upload.
            </span>
            {issues && issues.mismatchByColumn.length > 0 && (
              <ul
                aria-label="Fields with unmatched values"
                className="mt-2 flex flex-wrap gap-x-4 gap-y-1"
              >
                {issues.mismatchByColumn.map(col => (
                  <li key={col.column} className="text-[13px] text-foreground">
                    <span className="font-medium">{col.column}</span> —{' '}
                    {col.count} row{col.count !== 1 ? 's' : ''}
                  </li>
                ))}
              </ul>
            )}
            {issues?.truncated && (
              <p className="mt-1 text-[12px] text-muted-foreground">
                Showing a partial scan of the file — not all mismatches may be
                listed.
              </p>
            )}
          </AlertDescription>
        </Alert>
      )}

      {hasRejected && (
        <Alert variant="destructive">
          <AlertCircle aria-hidden="true" className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>
              {productList.invalid_rows} row
              {productList.invalid_rows !== 1 ? 's were' : ' was'}{' '}
              {isUpdate
                ? 'skipped. These rows will not update existing items or add new ones.'
                : 'rejected due to validation errors.'}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="ml-2 shrink-0 cursor-pointer"
              onClick={handleDownloadErrorReport}
            >
              <Download aria-hidden="true" className="h-3 w-3 mr-1" />
              Download CSV
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {hasWarnings && (
        <Alert variant="warning">
          <AlertTriangle aria-hidden="true" className="h-4 w-4" />
          <AlertDescription>
            Some imported rows had warnings — certain fields were set to null or
            rounded.
          </AlertDescription>
        </Alert>
      )}

      {hasCorrections && (
        <Alert variant="info">
          <ListChecks aria-hidden="true" className="h-4 w-4" />
          <AlertDescription>
            {correctionMappings.length} hierarchy value
            {correctionMappings.length !== 1 ? 's were' : ' was'} automatically
            corrected. Review the Corrections tab below — these don&apos;t block
            the import.
          </AlertDescription>
        </Alert>
      )}

      {hasIssues ? (
        <Tabs defaultValue={defaultTab}>
          <TabsList className="w-full flex-wrap gap-1 group-data-[orientation=horizontal]/tabs:h-auto">
            <TabsTrigger value="valid" className="cursor-pointer">
              Valid Items ({validPagination.totalCount})
            </TabsTrigger>
            {hasRejected && (
              <TabsTrigger value="rejected" className="cursor-pointer">
                <AlertCircle className="h-3.5 w-3.5 mr-1 text-red-600" />
                Rejected ({productList.invalid_rows})
              </TabsTrigger>
            )}
            {hasMismatches && (
              <TabsTrigger value="mismatches" className="cursor-pointer">
                <Ban className="h-3.5 w-3.5 mr-1 text-orange-600" />
                Value Mismatches ({mismatchItems.length})
              </TabsTrigger>
            )}
            {hasWarnings && (
              <TabsTrigger value="warnings" className="cursor-pointer">
                <AlertTriangle className="h-3.5 w-3.5 mr-1 text-amber-600" />
                Warnings ({otherWarningItems.length})
              </TabsTrigger>
            )}
            {hasCorrections && (
              <TabsTrigger value="corrections" className="cursor-pointer">
                <ListChecks
                  aria-hidden="true"
                  className="h-3.5 w-3.5 mr-1 text-blue-600"
                />
                Corrections ({correctionMappings.length})
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="valid" className="mt-2">
            {validItemsTable}
          </TabsContent>

          {hasRejected && (
            <TabsContent value="rejected" className="mt-2">
              <IssueTable
                columns={REJECTED_COLUMNS}
                data={rejectedData?.data ?? []}
                totalCount={rejectedData?.pagination.total ?? 0}
                page={rejectedPage}
                pageSize={rejectedPageSize}
                onPageChange={setRejectedPage}
                onPageSizeChange={setRejectedPageSize}
                isLoading={rejectedLoading}
                emptyMessage="No rejected rows."
              />
            </TabsContent>
          )}

          {hasMismatches && (
            <TabsContent value="mismatches" className="mt-2">
              <IssueTable
                columns={MISMATCH_COLUMNS}
                data={mismatchPageItems}
                totalCount={mismatchItems.length}
                page={mismatchPage}
                pageSize={mismatchPageSize}
                onPageChange={setMismatchPage}
                onPageSizeChange={setMismatchPageSize}
                emptyMessage="No value mismatches."
              />
            </TabsContent>
          )}

          {hasWarnings && (
            <TabsContent value="warnings" className="mt-2">
              <IssueTable
                columns={WARNING_COLUMNS}
                data={warningPageItems}
                totalCount={otherWarningItems.length}
                page={warningPage}
                pageSize={warningPageSize}
                onPageChange={setWarningPage}
                onPageSizeChange={setWarningPageSize}
                emptyMessage="No warnings."
              />
            </TabsContent>
          )}

          {hasCorrections && (
            <TabsContent value="corrections" className="mt-2 space-y-3">
              <p className="text-xs text-muted-foreground">
                Each mapping is shown once, with the file rows where it was
                applied.
              </p>
              <div className="border rounded-lg overflow-hidden [&_td]:align-top">
                <PaginatedTable<HierarchyCorrectionMapping>
                  itemLabel="corrections"
                  pageSizeLabel="Corrections per page:"
                  columns={CORRECTION_COLUMNS}
                  data={correctionPageItems}
                  page={correctionPage}
                  pageSize={correctionPageSize}
                  totalCount={correctionMappings.length}
                  totalPages={Math.max(
                    1,
                    Math.ceil(correctionMappings.length / correctionPageSize)
                  )}
                  pageSizeOptions={[5, 10, 25]}
                  onPageChange={setCorrectionPage}
                  onPageSizeChange={setCorrectionPageSize}
                  getRowKey={mapping => mapping.id}
                  emptyMessage="No hierarchy corrections."
                />
              </div>
            </TabsContent>
          )}
        </Tabs>
      ) : (
        validItemsTable
      )}
    </div>
  )
}
